import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { useLanguage } from "../i18n/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Loader2, Mail, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { regionConfig, getAuthProviders } from "../config/regions";
import LanguageSwitcher from "../components/LanguageSwitcher";
import BrandLockup from "../components/BrandLockup";
import LegalTermsNotice from "../components/LegalTermsNotice";
import { useClinicalShellTheme } from "../hooks/useBrandShellTheme";

const GoogleIcon = () => (
  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

const AppleIcon = () => (
  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.53-3.23 0-1.44.66-2.2.47-3.06-.4C3.79 16.17 4.36 9.03 8.72 8.78c1.15.06 1.95.67 2.62.72.99-.2 1.95-.78 3.01-.7 1.28.1 2.24.58 2.86 1.49-2.63 1.57-2.01 5.04.31 6.01-.53 1.36-1.22 2.72-2.47 3.98zM12.03 8.7c-.14-2.44 1.9-4.5 4.18-4.7.33 2.79-2.53 4.87-4.18 4.7z"/>
  </svg>
);

const YandexIcon = () => (
  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
    <path d="M13.32 21.634h2.476V2.366H12.89c-4.498 0-6.854 2.399-6.854 5.825 0 2.88 1.41 4.528 3.928 6.215l-4.37 7.228h2.72l4.737-8.035-1.37-.892c-2.073-1.37-3.084-2.56-3.084-4.72 0-2.237 1.53-3.724 4.253-3.724h.47v17.37z"/>
  </svg>
);

const ProviderIcon = ({ provider }) => {
  switch (provider) {
    case "google": return <GoogleIcon />;
    case "apple": return <AppleIcon />;
    case "yandex": return <YandexIcon />;
    default: return null;
  }
};

const SignupPage = () => {
  useClinicalShellTheme();
  const { user, loading } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const [signingIn, setSigningIn] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);

  const providers = getAuthProviders();
  const oauthProviders = providers.filter((p) => p.type === "oauth");
  const hasEmailAuth = providers.some((p) => p.id === "email");
  const emailOnly = oauthProviders.length === 0 && hasEmailAuth;

  useEffect(() => {
    if (user && !loading) {
      navigate("/case/new", { replace: true });
    }
  }, [user, loading, navigate]);

  const handleOAuthLogin = async (providerId) => {
    setSigningIn(true);
    try {
      if (providerId === "yandex") {
        window.location.href = `${API}/auth/yandex/login`;
        return;
      }
      const { supabase } = await import("../lib/supabase");
      if (!supabase) {
        toast.error(t("login.oauthNotConfigured") || "This sign-in method is not available.");
        setSigningIn(false);
        return;
      }
      const { error } = await supabase.auth.signInWithOAuth({
        provider: providerId,
        options: { redirectTo: `${window.location.origin}/auth/callback` },
      });
      if (error) {
        toast.error(error.message);
        setSigningIn(false);
      }
    } catch (err) {
      toast.error(t("login.error") || "Sign in failed");
      setSigningIn(false);
    }
  };

  const handleEmailSignup = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;

    setEmailLoading(true);
    try {
      const payload = { email: email.trim(), password };
      if (displayName.trim()) payload.name = displayName.trim();
      const resp = await axios.post(`${API}/auth/signup`, payload);
      toast.success(
        resp.data?.data?.message ||
          t("login.checkEmail") ||
          "Check your email for the confirmation link"
      );
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || t("login.error") || "Signup failed");
    } finally {
      setEmailLoading(false);
    }
  };

  const complianceLabel =
    regionConfig.compliance_label?.[language] || regionConfig.compliance_label?.en || "";
  const piiLabel = regionConfig.pii_label?.[language] || regionConfig.pii_label?.en || "PII Redacted";

  const renderEmailForm = () => (
    <form onSubmit={handleEmailSignup} className="space-y-4" data-testid="signup-email-form">
      {!emailOnly && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="gap-1.5 text-muted-foreground hover:text-foreground -ml-2 mb-1"
          onClick={() => setShowEmailForm(false)}
        >
          {t("common.back") || "Back"}
        </Button>
      )}

      <div className="space-y-3">
        <Input
          type="text"
          placeholder={t("login.displayNameOptional")}
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          className="h-11 bg-background"
          autoComplete="name"
        />
        <Input
          data-testid="signup-email-input"
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="h-11 bg-background"
          autoComplete="email"
        />
        <div className="relative">
          <Input
            data-testid="signup-password-input"
            type={showPassword ? "text" : "password"}
            placeholder={t("login.password") || "Password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            className="h-11 bg-background pr-10"
            autoComplete="new-password"
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setShowPassword(!showPassword)}
            tabIndex={-1}
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <Button
        data-testid="signup-submit-btn"
        type="submit"
        disabled={emailLoading}
        className="w-full h-12 font-semibold"
      >
        {emailLoading && <Loader2 className="w-5 h-5 mr-2 animate-spin" />}
        {t("login.register") || "Create account"}
      </Button>

      <p className="text-center text-xs text-muted-foreground">
        {t("login.hasAccount") || "Already have an account?"}{" "}
        <Link to="/login" className="text-primary hover:underline">
          {t("login.signIn") || "Sign in"}
        </Link>
      </p>
    </form>
  );

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="signup-page">
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-xl space-y-10 flex flex-col items-center">
          <div className="text-center space-y-4">
            <h1 className="font-display text-6xl md:text-7xl lg:text-8xl tracking-tight leading-none font-bold">
              <BrandLockup
                as="span"
                markSize={72}
                className="font-display text-6xl md:text-7xl lg:text-8xl tracking-tight leading-none font-bold justify-center"
              />
            </h1>
            <p className="text-muted-foreground text-sm tracking-widest uppercase font-medium">
              {t("login.subtitle") || "Clinical Decision Support"}
            </p>
          </div>

          <div className="w-full max-w-sm bg-card border border-border rounded-lg p-8">
            <div className="space-y-5">
              <div className="text-center space-y-2">
                <h2 className="font-display text-xl">
                  {t("login.signUpTitle")}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {t("login.signUpDesc")}
                </p>
              </div>

              {emailOnly ? (
                renderEmailForm()
              ) : showEmailForm ? (
                renderEmailForm()
              ) : (
                <div className="space-y-3">
                  {oauthProviders.map((provider) => (
                    <Button
                      key={provider.id}
                      data-testid={`signup-${provider.id}-btn`}
                      onClick={() => handleOAuthLogin(provider.id)}
                      disabled={signingIn || loading}
                      className="w-full h-12 font-semibold"
                    >
                      {signingIn ? (
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      ) : (
                        <ProviderIcon provider={provider.id} />
                      )}
                      {signingIn
                        ? (t("login.signingIn") || "Signing in...")
                        : (provider.label[language] || provider.label.en)}
                    </Button>
                  ))}

                  {hasEmailAuth && (
                    <>
                      <div className="relative my-2">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t border-border" />
                        </div>
                        <div className="relative flex justify-center text-xs">
                          <span className="bg-card px-3 text-muted-foreground">
                            {t("login.or") || "or"}
                          </span>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        onClick={() => setShowEmailForm(true)}
                        className="w-full h-12 font-semibold border-border hover:border-primary/40 transition-colors duration-200"
                      >
                        <Mail className="w-5 h-5 mr-2" />
                        {t("login.signUpWithEmail")}
                      </Button>
                    </>
                  )}

                  <p className="text-center text-xs text-muted-foreground pt-2">
                    {t("login.hasAccount") || "Already have an account?"}{" "}
                    <Link to="/login" className="text-primary hover:underline">
                      {t("login.signIn") || "Sign in"}
                    </Link>
                  </p>
                </div>
              )}
            </div>
          </div>

          <LegalTermsNotice variant="signup" />

          <LanguageSwitcher
            className="[&_button]:border [&_button]:border-border [&_button]:text-muted-foreground [&_button]:hover:text-foreground [&_button]:hover:border-primary/40"
          />

          <div className="flex items-center justify-center gap-6 text-[11px] text-muted-foreground uppercase tracking-wider">
            {complianceLabel ? <span>{complianceLabel}</span> : null}
            {complianceLabel ? <span className="text-border">|</span> : null}
            <span>{piiLabel}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
